"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { ArrowRight, Crosshair } from "lucide-react";
const capabilities = [{
  id: "cap-1",
  title: "Smart Cameras",
  description: "High-definition surveillance with AI-powered motion detection and night vision for total home awareness.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687986/img/smart_security_cameras.png",
  imageSrcSet: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687986/img/smart_security_cameras.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687986/img/smart_security_cameras.png-wvc-sizes",
  link: "#"
}, {
  id: "cap-2",
  title: "Smart Locks",
  description: "Keyless entry solutions that integrate seamlessly with your mobile device for secure, remote access control.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687986/img/digital_smart_locks.png",
  imageSrcSet: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687986/img/digital_smart_locks.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687986/img/digital_smart_locks.png-wvc-sizes",
  link: "#"
}, {
  id: "cap-3",
  title: "Alarm Systems",
  description: "Comprehensive intrusion detection networks with 24/7 professional monitoring for ultimate peace of mind.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687986/img/home_alarm_systems.png",
  imageSrcSet: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687986/img/home_alarm_systems.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687986/img/home_alarm_systems.png-wvc-sizes",
  link: "#"
}, {
  id: "cap-4",
  title: "Video Doorbells",
  description: "See, hear, and speak to visitors from anywhere with crystal-clear two-way audio and wide-angle video.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687986/img/video_doorbell_tech.png",
  imageSrcSet: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687986/img/video_doorbell_tech.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687986/img/video_doorbell_tech.png-wvc-sizes",
  link: "#"
}, {
  id: "cap-5",
  title: "Security Systems",
  description: "All-in-one DIY and professional security packages tailored to fit your family's specific living space.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687986/img/integrated_security_hub.png",
  imageSrcSet: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687986/img/integrated_security_hub.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687986/img/integrated_security_hub.png-wvc-sizes",
  link: "#"
}, {
  id: "cap-6",
  title: "Smoke Detectors",
  description: "Advanced fire and carbon monoxide sensors that alert your phone instantly during an emergency.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687986/img/smart_smoke_sensors.png",
  imageSrcSet: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687986/img/smart_smoke_sensors.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687986/img/smart_smoke_sensors.png-wvc-sizes",
  link: "#"
}];
export default function ExploreSafetyCategories() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="254910" id="explore-safety-categories" className="bg-background py-24 text-foreground">
      <div className="container mx-auto px-4 md:px-6">
        <div ref={ref} className={`mb-16 flex flex-col items-center text-center transition-all duration-1000 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
          <div className="mb-4 flex items-center gap-2 text-destructive">
            <Crosshair className="size-5" />
            <span className="font-default text-sm font-bold tracking-widest uppercase">
              Expert Reviews
            </span>
          </div>
          <h2 className="font-default text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl text-primary">
            Explore Safety Categories
          </h2>
          <div className="mt-6 h-1 w-24 bg-secondary"></div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {capabilities.map((cap, i) => <Link key={cap.id} to={cap.link} data-index={i} className={`group relative block min-h-[400px] overflow-hidden bg-card rounded-2xl shadow-md transition-all duration-700 hover:shadow-xl hover:-translate-y-1 md:min-h-[480px] ${isVisible ? "translate-y-0 opacity-100" : "translate-y-16 opacity-0"}`} style={{
          transitionDelay: `${i * 100}ms`
        }}>
              {}
              <img src={cap.imageSrc} data-wvc-srcset={cap.imageSrcSet} data-wvc-sizes={cap.imageSizes} alt={cap.title} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />

              {}
              <div className="absolute inset-0 bg-primary/60 transition-colors duration-500 group-hover:bg-primary/40" />

              {}
              <div className="relative z-10 flex h-full flex-col justify-end p-8">
                <div className="mb-4 flex items-center gap-1">
                  {[...Array(5)].map((_, starIndex) => <span key={starIndex} className="text-secondary text-sm"></span>)}
                </div>
                <h3 className="font-default mb-3 text-2xl font-bold tracking-wide text-white md:text-3xl">
                  {cap.title}
                </h3>
                <p className="font-default mb-6 text-base leading-relaxed text-white/90 opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                  {cap.description}
                </p>
                <div className="flex items-center justify-center w-full py-3 px-6 bg-secondary rounded-lg font-default text-sm font-bold text-primary transition-transform duration-300 group-hover:scale-105">
                  <span>VIEW BEST OPTIONS</span>
                  <ArrowRight className="ml-2 size-4" />
                </div>
              </div>
            </Link>)}
        </div>
      </div>
    </section>;
}